import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, Plus, Search, Calendar, DollarSign, Building2, Users,
  Edit, Eye, Trash2, Filter, Download, AlertCircle, CheckCircle,
  XCircle, Clock, MapPin, Phone, FileCheck, ArrowRight, Home, ArrowUp, ArrowDown
} from 'lucide-react';
import { ResPDFGenerator } from '../../utils/pdfGenerator';

const Rentals = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedRental, setSelectedRental] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeSection, setActiveSection] = useState('renting-out'); // 'renting-out' or 'renting'

  const statusOptions = ['all', 'active', 'expired', 'expiring-soon', 'pending'];

  // RENTING OUT - Properties we own and rent to tenants
  const rentingOutContracts = [
    {
      id: 'RENT-OUT-001',
      propertyId: 'PROP-001',
      propertyName: 'Riyadh Main Store',
      propertyInternalId: 'LOC-RYD-001',
      tenantId: 'TEN-001',
      tenantName: 'ABC Retail Company',
      tenantType: 'company',
      tenantContact: '+966 50 123 4567',
      tenantEmail: 'contact@abcretail.com',
      contractNumber: 'CNT-2023-001',
      startDate: '2023-01-15',
      endDate: '2026-01-14',
      duration: '3 years',
      monthlyRent: 15000,
      securityDeposit: 45000,
      paymentTerms: 'Monthly, due on 15th',
      paymentMethod: 'Bank Transfer',
      autoRenewal: true,
      renewalNoticeDays: 90,
      status: 'active',
      daysUntilExpiry: 700,
      totalPayments: 18,
      paymentsReceived: 18,
      paymentsPending: 0,
      totalAmount: 270000,
      amountReceived: 270000,
      amountPending: 0,
      lastPaymentDate: '2024-01-15',
      nextPaymentDate: '2024-02-15',
      daysUntilNextPayment: 2,
      specialTerms: 'Annual rent increase of 5%',
      notes: 'Long-term tenant, excellent payment history',
      city: 'Riyadh'
    },
    {
      id: 'RENT-OUT-002',
      propertyId: 'PROP-002',
      propertyName: 'Jeddah Warehouse Complex',
      propertyInternalId: 'LOC-JED-001',
      tenantId: 'TEN-002',
      tenantName: 'XYZ Logistics',
      tenantType: 'company',
      tenantContact: '+966 50 987 6543',
      tenantEmail: 'info@xyzlogistics.com',
      contractNumber: 'CNT-2022-045',
      startDate: '2022-06-01',
      endDate: '2025-05-31',
      duration: '3 years',
      monthlyRent: 25000,
      securityDeposit: 75000,
      paymentTerms: 'Monthly, due on 1st',
      paymentMethod: 'Bank Transfer',
      autoRenewal: false,
      renewalNoticeDays: 60,
      status: 'active',
      daysUntilExpiry: 470,
      totalPayments: 20,
      paymentsReceived: 19,
      paymentsPending: 1,
      totalAmount: 500000,
      amountReceived: 475000,
      amountPending: 25000,
      lastPaymentDate: '2024-01-01',
      nextPaymentDate: '2024-02-01',
      daysUntilNextPayment: -11,
      specialTerms: 'Quarterly maintenance included',
      notes: 'Payment overdue, follow up required',
      city: 'Jeddah'
    },
    {
      id: 'RENT-OUT-003',
      propertyId: 'PROP-003',
      propertyName: 'Dammam Office Building',
      propertyInternalId: 'LOC-DMM-001',
      tenantId: 'TEN-003',
      tenantName: 'Tech Solutions Inc.',
      tenantType: 'company',
      tenantContact: '+966 50 555 1234',
      tenantEmail: 'admin@techsolutions.com',
      contractNumber: 'CNT-2023-012',
      startDate: '2023-03-01',
      endDate: '2026-02-28',
      duration: '3 years',
      monthlyRent: 18000,
      securityDeposit: 54000,
      paymentTerms: 'Monthly, due on 10th',
      paymentMethod: 'Bank Transfer',
      autoRenewal: true,
      renewalNoticeDays: 90,
      status: 'active',
      daysUntilExpiry: 750,
      totalPayments: 12,
      paymentsReceived: 11,
      paymentsPending: 1,
      totalAmount: 216000,
      amountReceived: 198000,
      amountPending: 18000,
      lastPaymentDate: '2024-01-10',
      nextPaymentDate: '2024-02-10',
      daysUntilNextPayment: -3,
      specialTerms: 'Parking spaces included',
      notes: 'Payment overdue, send reminder',
      city: 'Dammam'
    },
    {
      id: 'RENT-OUT-004',
      propertyId: 'PROP-005',
      propertyName: 'Khobar Storage Facility',
      propertyInternalId: 'LOC-KBU-001',
      tenantId: 'TEN-004',
      tenantName: 'Storage Co.',
      tenantType: 'company',
      tenantContact: '+966 50 444 5678',
      tenantEmail: 'contact@storageco.com',
      contractNumber: 'CNT-2021-003',
      startDate: '2021-01-01',
      endDate: '2024-12-31',
      duration: '4 years',
      monthlyRent: 20000,
      securityDeposit: 80000,
      paymentTerms: 'Monthly, due on 15th',
      paymentMethod: 'Bank Transfer',
      autoRenewal: false,
      renewalNoticeDays: 60,
      status: 'expiring-soon',
      daysUntilExpiry: 320,
      totalPayments: 48,
      paymentsReceived: 48,
      paymentsPending: 0,
      totalAmount: 960000,
      amountReceived: 960000,
      amountPending: 0,
      lastPaymentDate: '2024-01-15',
      nextPaymentDate: '2024-02-15',
      daysUntilNextPayment: 2,
      specialTerms: 'Early termination penalty applies',
      notes: 'Contract expiring soon, discuss renewal',
      city: 'Khobar'
    },
  ];

  // RENTING - Properties we rent from landlords
  const rentingContracts = [
    {
      id: 'RENT-IN-001',
      propertyId: 'PROP-OUR-001',
      propertyName: 'Corporate Office - Olaya',
      propertyInternalId: 'OUR-OFFICE-001',
      landlordId: 'LAND-001',
      landlordName: 'Al-Rashid Properties',
      landlordType: 'company',
      landlordContact: '+966 11 234 5678',
      landlordEmail: 'info@alrashidproperties.com',
      contractNumber: 'CNT-OUR-2022-001',
      startDate: '2022-01-01',
      endDate: '2025-12-31',
      duration: '4 years',
      monthlyRent: 25000,
      securityDeposit: 100000,
      paymentTerms: 'Monthly, due on 5th',
      paymentMethod: 'Bank Transfer',
      autoRenewal: false,
      renewalNoticeDays: 90,
      status: 'active',
      daysUntilExpiry: 654,
      totalPayments: 24,
      paymentsMade: 24,
      paymentsPending: 0,
      totalAmount: 600000,
      amountPaid: 600000,
      amountPending: 0,
      lastPaymentDate: '2024-01-05',
      nextPaymentDate: '2024-02-05',
      daysUntilNextPayment: -8,
      specialTerms: 'Includes parking and utilities',
      notes: 'Main corporate office, payment overdue',
      city: 'Riyadh'
    },
    {
      id: 'RENT-IN-002',
      propertyId: 'PROP-OUR-002',
      propertyName: 'Warehouse - Dammam Industrial',
      propertyInternalId: 'OUR-WH-001',
      landlordId: 'LAND-002',
      landlordName: 'Industrial Real Estate Co.',
      landlordType: 'company',
      landlordContact: '+966 13 456 7890',
      landlordEmail: 'contact@industrialre.com',
      contractNumber: 'CNT-OUR-2023-005',
      startDate: '2023-01-01',
      endDate: '2026-06-30',
      duration: '3.5 years',
      monthlyRent: 20000,
      securityDeposit: 60000,
      paymentTerms: 'Monthly, due on 12th',
      paymentMethod: 'Bank Transfer',
      autoRenewal: true,
      renewalNoticeDays: 60,
      status: 'active',
      daysUntilExpiry: 870,
      totalPayments: 13,
      paymentsMade: 12,
      paymentsPending: 1,
      totalAmount: 260000,
      amountPaid: 240000,
      amountPending: 20000,
      lastPaymentDate: '2024-01-12',
      nextPaymentDate: '2024-02-12',
      daysUntilNextPayment: -1,
      specialTerms: 'Loading dock access included',
      notes: 'Payment due, send today',
      city: 'Dammam'
    },
  ];

  const currentContracts = activeSection === 'renting-out' ? rentingOutContracts : rentingContracts;
  
  const filteredContracts = currentContracts.filter(contract => {
    const matchesSearch = contract.contractNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.propertyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (activeSection === 'renting-out' ? contract.tenantName.toLowerCase().includes(searchQuery.toLowerCase()) :
       contract.landlordName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      contract.propertyInternalId.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesFilter = true;
    if (selectedFilter === 'expiring-soon') {
      matchesFilter = contract.daysUntilExpiry <= 365 && contract.status === 'active';
    } else if (selectedFilter !== 'all') {
      matchesFilter = contract.status === selectedFilter;
    }
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'expired': return 'bg-red-100 text-red-700';
      case 'expiring-soon': return 'bg-orange-100 text-orange-700';
      case 'pending': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPaymentStatusColor = (daysUntilPayment) => {
    if (daysUntilPayment === null) return 'text-gray-500';
    if (daysUntilPayment < 0) return 'text-red-600 font-semibold';
    if (daysUntilPayment <= 7) return 'text-orange-600 font-semibold';
    return 'text-green-600';
  };

  const ContractCard = ({ contract }) => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02, y: -2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all cursor-pointer"
        onClick={() => setSelectedRental(contract)}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">{contract.propertyName}</h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                {contract.status === 'expiring-soon' ? 'Expiring Soon' : contract.status}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-1">{contract.contractNumber}</p>
            <p className="text-sm text-gray-600 mb-4">{contract.propertyInternalId}</p>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {activeSection === 'renting-out' ? 'Tenant' : 'Landlord'}
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {activeSection === 'renting-out' ? contract.tenantName : contract.landlordName}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Monthly {activeSection === 'renting-out' ? 'Rent' : 'Payment'}</span>
                <span className="text-sm font-semibold text-gray-900">SAR {contract.monthlyRent.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Contract Period</span>
                <span className="text-sm text-gray-900">{contract.startDate} to {contract.endDate}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Duration</span>
                <span className="text-sm text-gray-900">{contract.duration}</span>
              </div>
              {contract.nextPaymentDate && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Next {activeSection === 'renting-out' ? 'Payment Due' : 'Payment'}</span>
                  <span className={`text-sm font-medium ${getPaymentStatusColor(contract.daysUntilNextPayment)}`}>
                    {contract.nextPaymentDate}
                    {contract.daysUntilNextPayment !== null && (
                      <span className="ml-2">
                        ({contract.daysUntilNextPayment < 0 ? `${Math.abs(contract.daysUntilNextPayment)} days overdue` :
                          contract.daysUntilNextPayment === 0 ? 'Due today' :
                          `${contract.daysUntilNextPayment} days`})
                      </span>
                    )}
                  </span>
                </div>
              )}
              {contract.status === 'active' && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Days Until Expiry</span>
                  <span className={`text-sm font-medium ${contract.daysUntilExpiry <= 365 ? 'text-orange-600' : 'text-gray-900'}`}>
                    {contract.daysUntilExpiry} days
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {(activeSection === 'renting-out' ? contract.amountPending : contract.amountPending) > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">
                SAR {(activeSection === 'renting-out' ? contract.amountPending : contract.amountPending).toLocaleString()} pending
              </span>
            </div>
          </div>
        )}

        {contract.status === 'expiring-soon' && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-orange-600">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">Contract expires in {contract.daysUntilExpiry} days</span>
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  const totalRentingOut = rentingOutContracts.reduce((sum, c) => sum + c.monthlyRent, 0);
  const totalRenting = rentingContracts.reduce((sum, c) => sum + c.monthlyRent, 0);
  const netRevenue = totalRentingOut - totalRenting;

  return (
    <div className="space-y-6">
      {/* Section Toggle */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Rentals & Contracts</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage rental agreements and contracts
            </p>
          </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Contract
            </motion.button>
        </div>

        {/* Two Section Toggle */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveSection('renting-out')}
            className={`p-6 rounded-xl border-2 transition-all ${
              activeSection === 'renting-out'
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-3 rounded-lg ${activeSection === 'renting-out' ? 'bg-green-100' : 'bg-gray-100'}`}>
                <ArrowUp className={`w-6 h-6 ${activeSection === 'renting-out' ? 'text-green-600' : 'text-gray-600'}`} />
              </div>
              <div className="text-left">
                <h3 className={`font-semibold ${activeSection === 'renting-out' ? 'text-green-700' : 'text-gray-700'}`}>
                  Renting Out
                </h3>
                <p className="text-xs text-gray-600">Properties we own</p>
              </div>
            </div>
            <div className="text-left">
              <p className="text-2xl font-bold text-gray-900">SAR {totalRentingOut.toLocaleString()}</p>
              <p className="text-xs text-gray-600">Monthly revenue</p>
            </div>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveSection('renting')}
            className={`p-6 rounded-xl border-2 transition-all ${
              activeSection === 'renting'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-3 rounded-lg ${activeSection === 'renting' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <ArrowDown className={`w-6 h-6 ${activeSection === 'renting' ? 'text-blue-600' : 'text-gray-600'}`} />
              </div>
              <div className="text-left">
                <h3 className={`font-semibold ${activeSection === 'renting' ? 'text-blue-700' : 'text-gray-700'}`}>
                  Renting
                </h3>
                <p className="text-xs text-gray-600">Properties we rent</p>
              </div>
            </div>
            <div className="text-left">
              <p className="text-2xl font-bold text-gray-900">SAR {totalRenting.toLocaleString()}</p>
              <p className="text-xs text-gray-600">Monthly expense</p>
            </div>
          </motion.button>
        </div>

        {/* Net Revenue Banner */}
        <div className={`p-4 rounded-lg ${netRevenue >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Net Monthly Revenue</p>
              <p className={`text-2xl font-bold ${netRevenue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                SAR {netRevenue.toLocaleString()}
              </p>
            </div>
            <DollarSign className={`w-8 h-8 ${netRevenue >= 0 ? 'text-green-600' : 'text-red-600'}`} />
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mt-6 flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={`Search ${activeSection === 'renting-out' ? 'renting out' : 'renting'} contracts...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-full"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {statusOptions.map((status) => (
              <button
                key={status}
                onClick={() => setSelectedFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedFilter === status
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'expiring-soon' ? 'Expiring Soon' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Contracts</p>
              <p className="text-2xl font-bold text-gray-900">
                {currentContracts.filter(r => r.status === 'active').length}
              </p>
            </div>
            <FileText className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                Monthly {activeSection === 'renting-out' ? 'Revenue' : 'Expense'}
              </p>
              <p className={`text-2xl font-bold ${activeSection === 'renting-out' ? 'text-green-600' : 'text-blue-600'}`}>
                SAR {(activeSection === 'renting-out' ? totalRentingOut : totalRenting).toLocaleString()}
              </p>
            </div>
            <DollarSign className={`w-8 h-8 ${activeSection === 'renting-out' ? 'text-green-600' : 'text-blue-600'}`} />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Payments</p>
              <p className="text-2xl font-bold text-red-600">
                SAR {currentContracts.reduce((sum, r) => sum + r.amountPending, 0).toLocaleString()}
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Expiring Soon</p>
              <p className="text-2xl font-bold text-orange-600">
                {currentContracts.filter(r => r.status === 'expiring-soon' || (r.status === 'active' && r.daysUntilExpiry <= 365)).length}
              </p>
            </div>
            <Clock className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Contracts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredContracts.map((contract) => (
          <ContractCard key={contract.id} contract={contract} />
        ))}
      </div>

      {/* Contract Detail Modal */}
      {selectedRental && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-xl font-bold text-gray-900">
                {activeSection === 'renting-out' ? 'Renting Out' : 'Renting'} Contract Details
              </h3>
              <button
                onClick={() => setSelectedRental(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Contract Info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-700">Contract Number</label>
                  <p className="text-lg text-gray-900">{selectedRental.contractNumber}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedRental.status)}`}>
                    {selectedRental.status === 'expiring-soon' ? 'Expiring Soon' : selectedRental.status}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Property</label>
                  <p className="text-lg text-gray-900">{selectedRental.propertyName}</p>
                  <p className="text-sm text-gray-600">{selectedRental.propertyInternalId}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    {activeSection === 'renting-out' ? 'Tenant' : 'Landlord'}
                  </label>
                  <p className="text-lg text-gray-900">
                    {activeSection === 'renting-out' ? selectedRental.tenantName : selectedRental.landlordName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Start Date</label>
                  <p className="text-lg text-gray-900">{selectedRental.startDate}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">End Date</label>
                  <p className="text-lg text-gray-900">{selectedRental.endDate}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Duration</label>
                  <p className="text-lg text-gray-900">{selectedRental.duration}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Days Until Expiry</label>
                  <p className={`text-lg font-semibold ${selectedRental.daysUntilExpiry <= 365 ? 'text-orange-600' : 'text-gray-900'}`}>
                    {selectedRental.daysUntilExpiry} days
                  </p>
                </div>
              </div>

              {/* Financial Info */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Financial Information</h4>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Monthly {activeSection === 'renting-out' ? 'Rent' : 'Payment'}</label>
                    <p className="text-lg font-semibold text-gray-900">SAR {selectedRental.monthlyRent.toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Security Deposit</label>
                    <p className="text-lg text-gray-900">SAR {selectedRental.securityDeposit.toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Total Contract Value</label>
                    <p className="text-lg font-semibold text-gray-900">SAR {selectedRental.totalAmount.toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      {activeSection === 'renting-out' ? 'Amount Received' : 'Amount Paid'}
                    </label>
                    <p className="text-lg text-green-600">
                      SAR {(activeSection === 'renting-out' ? selectedRental.amountReceived : selectedRental.amountPaid).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Amount Pending</label>
                    <p className={`text-lg font-semibold ${selectedRental.amountPending > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      SAR {selectedRental.amountPending.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Payment Terms</label>
                    <p className="text-lg text-gray-900">{selectedRental.paymentTerms}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Payment Method</label>
                    <p className="text-lg text-gray-900">{selectedRental.paymentMethod}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Next Payment Date</label>
                    <p className={`text-lg font-semibold ${getPaymentStatusColor(selectedRental.daysUntilNextPayment)}`}>
                      {selectedRental.nextPaymentDate}
                      {selectedRental.daysUntilNextPayment !== null && (
                        <span className="ml-2 text-sm">
                          ({selectedRental.daysUntilNextPayment < 0 ? `${Math.abs(selectedRental.daysUntilNextPayment)} days overdue` :
                            selectedRental.daysUntilNextPayment === 0 ? 'Due today' :
                            `${selectedRental.daysUntilNextPayment} days`})
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  {activeSection === 'renting-out' ? 'Tenant' : 'Landlord'} Contact
                </h4>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Phone</label>
                    <p className="text-lg text-gray-900">
                      {activeSection === 'renting-out' ? selectedRental.tenantContact : selectedRental.landlordContact}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Email</label>
                    <p className="text-lg text-gray-900">
                      {activeSection === 'renting-out' ? selectedRental.tenantEmail : selectedRental.landlordEmail}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contract Terms */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Contract Terms</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Auto Renewal</span>
                    <span className={`text-sm font-medium ${selectedRental.autoRenewal ? 'text-green-600' : 'text-gray-900'}`}>
                      {selectedRental.autoRenewal ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Renewal Notice Period</span>
                    <span className="text-sm text-gray-900">{selectedRental.renewalNoticeDays} days</span>
                  </div>
                  {selectedRental.specialTerms && (
                    <div className="mt-4">
                      <label className="text-sm font-medium text-gray-700">Special Terms</label>
                      <p className="text-lg text-gray-900 mt-2">{selectedRental.specialTerms}</p>
                    </div>
                  )}
                  {selectedRental.notes && (
                    <div className="mt-4">
                      <label className="text-sm font-medium text-gray-700">Notes</label>
                      <p className="text-lg text-gray-900 mt-2">{selectedRental.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6 flex gap-3">
                <button className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2">
                  <Edit className="w-4 h-4" />
                  Edit Contract
                </button>
                <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  View Documents
                </button>
                <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  View Invoices
                </button>
                <button 
                  onClick={() => {
                    const pdf = ResPDFGenerator.generateContract(selectedRental, activeSection);
                    pdf.save(`contract_${selectedRental.contractNumber}.pdf`);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Rentals;
